import type { AsyncReloadable } from "../../components/reloadable";
import type { BabylonState, Entity } from "../../state";

/**
 * Tells React whether any entity's data is loading, at most once per change.
 *
 * Runs in two places. `reloadStaleComponents` calls it right after starting
 * fetches, so a load is reported in the same task that began it, before its
 * fetch hands control back to the browser. React can then show the spinner
 * while the request is out, rather than a frame or more later. The render
 * loop calls it after every frame, which is where a load that finished in the
 * background is first seen to have ended.
 *
 * Only data fetches count: an `AsyncReloadable` in `"loading"`, which is a
 * position, rotation, vector field, model or joint value cache. A synchronous
 * rebuild (`Reloadable`) never enters `"loading"`, data that needs no fetch
 * resolves before this runs, and a component waiting for the next reload poll
 * is still `"stale"`.
 *
 * No-op for a manager built without an `onDataLoadingChanged` callback; the
 * ingest preview's manager has none.
 */
export function reportDataLoading(state: BabylonState): void {
  const onDataLoadingChanged = state.context.callbacks.onDataLoadingChanged;
  if (!onDataLoadingChanged) {
    return;
  }
  const isLoading = isAnyEntityLoading(state);
  if (isLoading === (state.sceneConfig.reportedDataLoading ?? false)) {
    return;
  }
  state.sceneConfig.reportedDataLoading = isLoading;
  onDataLoadingChanged(isLoading);
}

function isAnyEntityLoading(state: BabylonState): boolean {
  for (const entity of Object.values(state.entities)) {
    if (isEntityLoading(entity)) {
      return true;
    }
  }
  return false;
}

/** Whether any of the entity's asynchronously loaded components is fetching. */
function isEntityLoading(entity: Entity): boolean {
  const { position, rotation, vectorField, mesh } = entity;
  if (
    isFetching(position?.cache.load) ||
    isFetching(rotation?.cache.load) ||
    isFetching(vectorField?.cache.load) ||
    isFetching(mesh?.load)
  ) {
    return true;
  }
  for (const cache of Object.values(mesh?.jointCaches ?? {})) {
    if (isFetching(cache.load)) {
      return true;
    }
  }
  return false;
}

function isFetching(load: AsyncReloadable | undefined): boolean {
  return load?.state === "loading";
}
